/**
 * Stripe Connect integration utilities
 * Handles OAuth flow, token management, and API interactions
 */

import type { StripeIntegration } from '@/contexts/IntegrationsContext';
import { encryptString, decryptString } from './encryption';

/**
 * Stripe OAuth configuration
 */
export const STRIPE_OAUTH_CONFIG = {
  authorizeUrl: 'https://connect.stripe.com/oauth/authorize',
  tokenUrl: 'https://connect.stripe.com/oauth/token',
  scope: 'read_write', // Full access to the connected account
};

/**
 * Response from Stripe's token endpoint
 */
export interface StripeTokenResponse {
  access_token: string;
  livemode: boolean;
  refresh_token?: string;
  token_type: 'bearer';
  stripe_publishable_key: string;
  stripe_user_id: string;
  scope: string;
}

/**
 * Response from Stripe's account endpoint
 */
export interface StripeAccountResponse {
  id: string;
  object: 'account';
  business_profile?: {
    name?: string;
    url?: string;
  };
  email?: string;
  country?: string;
  [key: string]: unknown;
}

/**
 * Creates a Stripe integration object from OAuth token response
 */
export async function createStripeIntegration(
  id: string,
  name: string,
  tokenResponse: StripeTokenResponse,
  accountInfo?: StripeAccountResponse
): Promise<StripeIntegration> {
  // Encrypt the access token for secure storage
  const encryptedToken = await encryptString(tokenResponse.access_token);

  const now = Date.now();

  return {
    id,
    name,
    type: 'stripe',
    enabled: true,
    createdAt: now,
    updatedAt: now,
    accessToken: encryptedToken,
    stripeAccountId: tokenResponse.stripe_user_id,
    publishableKey: tokenResponse.stripe_publishable_key,
    livemode: tokenResponse.livemode,
    scopes: tokenResponse.scope ? tokenResponse.scope.split(',').map(s => s.trim()) : [],
    refreshToken: tokenResponse.refresh_token
      ? await encryptString(tokenResponse.refresh_token)
      : undefined,
    metadata: accountInfo
      ? {
          stripeUserId: accountInfo.id,
          businessName: accountInfo.business_profile?.name,
          email: accountInfo.email,
          country: accountInfo.country,
        }
      : undefined,
  };
}

/**
 * Decrypts and returns the Stripe access token
 */
export async function getStripeAccessToken(integration: StripeIntegration): Promise<string> {
  return decryptString(integration.accessToken);
}

/**
 * Decrypts and returns the Stripe refresh token (if available)
 */
export async function getStripeRefreshToken(
  integration: StripeIntegration
): Promise<string | undefined> {
  if (!integration.refreshToken) return undefined;
  return decryptString(integration.refreshToken);
}

/**
 * Fetches Stripe account information
 */
export async function fetchStripeAccount(
  accessToken: string
): Promise<StripeAccountResponse> {
  const response = await fetch('https://api.stripe.com/v1/account', {
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to fetch Stripe account: ${response.statusText} - ${error}`);
  }

  return response.json();
}

/**
 * Refreshes an expired Stripe access token using the refresh token
 */
export async function refreshStripeToken(
  clientId: string,
  refreshToken: string
): Promise<StripeTokenResponse> {
  const params = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
  });

  const response = await fetch(STRIPE_OAUTH_CONFIG.tokenUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${clientId}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to refresh Stripe token: ${response.statusText} - ${error}`);
  }

  return response.json();
}

/**
 * Revokes a Stripe Connect integration
 */
export async function revokeStripeIntegration(
  clientId: string,
  stripeAccountId: string
): Promise<void> {
  const params = new URLSearchParams({
    client_id: clientId,
    stripe_user_id: stripeAccountId,
  });

  const response = await fetch('https://connect.stripe.com/oauth/deauthorize', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to revoke Stripe integration: ${response.statusText} - ${error}`);
  }
}

/**
 * Validates that a Stripe integration is still active
 */
export async function validateStripeIntegration(
  integration: StripeIntegration
): Promise<boolean> {
  try {
    const accessToken = await getStripeAccessToken(integration);
    await fetchStripeAccount(accessToken);
    return true;
  } catch (error) {
    console.error('Stripe integration validation failed:', error);
    return false;
  }
}

/**
 * Fetches the secret key for a Stripe Connect integration
 * Note: This requires the access token to have the appropriate scope
 */
export async function fetchStripeSecretKey(accessToken: string): Promise<string | null> {
  try {
    // For Stripe Connect, the access token IS the restricted key that can be used
    // to make API calls on behalf of the connected account
    // We can use it directly as the "secret key" for serverless functions
    return accessToken;
  } catch (error) {
    console.error('Error fetching Stripe secret key:', error);
    return null;
  }
}

/**
 * Creates products in Stripe
 */
export async function createStripeProduct(
  accessToken: string,
  name: string,
  description?: string
): Promise<any> {
  const params = new URLSearchParams({
    name,
  });

  if (description) {
    params.append('description', description);
  }

  const response = await fetch('https://api.stripe.com/v1/products', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to create product: ${response.statusText} - ${error}`);
  }

  return response.json();
}

/**
 * Creates a price for a product in Stripe
 */
export async function createStripePrice(
  accessToken: string,
  productId: string,
  amount: number,
  currency: string = 'usd',
  recurring?: { interval: 'day' | 'week' | 'month' | 'year' }
): Promise<any> {
  const params = new URLSearchParams({
    product: productId,
    unit_amount: amount.toString(),
    currency,
  });

  if (recurring) {
    params.append('recurring[interval]', recurring.interval);
  }

  const response = await fetch('https://api.stripe.com/v1/prices', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to create price: ${response.statusText} - ${error}`);
  }

  return response.json();
}
